import { useEffect, useMemo } from "react";
import {
  CARTAZ_GRANDE,
  CARTAZ_MEDIO,
  CARTAZ_PEQUENO,
  FEED_INSTAGRAM,
  FEED_INSTAGRAM_PORTRAIT,
  PANFLETO,
  STORIES,
} from "../../CanvasViewer/EncarteConstants";

const FOOTER_STYLES = [
  {
    value: "FULL_DETAILS",
    label: "Todas as informações",
    isPremium: false,
  },
  { value: "HIDDEN", label: "Ocultar rodapé", isPremium: false },
  {
    value: "ADDRESS_AND_DATE_ONLY",
    label: "Apenas endereço e validade",
    isPremium: true,
  },
  {
    value: "SOCIAL_MEDIA_ONLY",
    label: "Apenas redes sociais",
    isPremium: true,
  },
  {
    value: "PAYMENT_METHODS_ONLY",
    label: "Apenas formas de pagamento",
    isPremium: true,
  },
  {
    value: "BRAND_NAME_AND_SLOGAN_ONLY",
    label: "Apenas nome e slogan da marca",
    isPremium: true,
  },
  {
    value: "DATE_ONLY",
    label: "Apenas data de validade",
    isPremium: true,
  },
  {
    value: "NOTICE_ONLY",
    label: "Apenas aviso",
    isPremium: true,
  },
  {
    value: "NOTICE_ADDRESS_AND_DATE",
    label: "Aviso, endereço e validade",
    isPremium: true,
  },
  {
    value: "NOTICE_SOCIAL_MEDIA",
    label: "Aviso e redes sociais",
    isPremium: true,
  },
];

const findFooterStyleByLabel = (label) => {
  return FOOTER_STYLES.find((footerStyle) => footerStyle.label === label);
};

const findFooterStyleByValue = (value) => {
  return FOOTER_STYLES.find((footerStyle) => footerStyle.value === value);
};

const FOOTER_STYLE_VALUES_BY_FORMAT = {
  [CARTAZ_PEQUENO]: ["DATE_ONLY", "HIDDEN"],
  [CARTAZ_MEDIO]: ["DATE_ONLY", "HIDDEN"],
  [CARTAZ_GRANDE]: ["DATE_ONLY", "HIDDEN"],
  [STORIES]: [
    "DATE_ONLY",
    "NOTICE_ONLY",
    "NOTICE_ADDRESS_AND_DATE",
    "NOTICE_SOCIAL_MEDIA",
    "HIDDEN",
    "FULL_DETAILS",
    "SOCIAL_MEDIA_ONLY",
    "PAYMENT_METHODS_ONLY",
    "BRAND_NAME_AND_SLOGAN_ONLY",
    "ADDRESS_AND_DATE_ONLY",
  ],
  [PANFLETO]: [
    "DATE_ONLY",
    "NOTICE_ONLY",
    "NOTICE_ADDRESS_AND_DATE",
    "NOTICE_SOCIAL_MEDIA",
    "HIDDEN",
    "FULL_DETAILS",
    "SOCIAL_MEDIA_ONLY",
    "PAYMENT_METHODS_ONLY",
    "BRAND_NAME_AND_SLOGAN_ONLY",
    "ADDRESS_AND_DATE_ONLY",
  ],
  [FEED_INSTAGRAM]: [
    "NOTICE_ADDRESS_AND_DATE",
    "NOTICE_SOCIAL_MEDIA",
    "DATE_ONLY",
    "NOTICE_ONLY",
    "HIDDEN",
    "SOCIAL_MEDIA_ONLY",
    "PAYMENT_METHODS_ONLY",
    "BRAND_NAME_AND_SLOGAN_ONLY",
    "ADDRESS_AND_DATE_ONLY",
  ],
  [FEED_INSTAGRAM_PORTRAIT]: [
    "NOTICE_ADDRESS_AND_DATE",
    "NOTICE_SOCIAL_MEDIA",
    "DATE_ONLY",
    "NOTICE_ONLY",
    "HIDDEN",
    "SOCIAL_MEDIA_ONLY",
    "PAYMENT_METHODS_ONLY",
    "BRAND_NAME_AND_SLOGAN_ONLY",
    "ADDRESS_AND_DATE_ONLY",
  ],
};

const getFooterStylesByFormat = (format) => {
  const allowedValues = FOOTER_STYLE_VALUES_BY_FORMAT[format];
  if (!allowedValues) return FOOTER_STYLES;

  return FOOTER_STYLES.filter((footerStyle) =>
    allowedValues.includes(footerStyle.value),
  );
};

const getFooterStyleLabelsByFormat = (format) => {
  return getFooterStylesByFormat(format).map(
    (footerStyle) => footerStyle.label,
  );
};

const isFooterStyleAllowedForFormat = (format, footerStyleValue) => {
  return getFooterStylesByFormat(format).some(
    (footerStyle) => footerStyle.value === footerStyleValue,
  );
};

const getDefaultFooterStyleByFormat = (format) => {
  const footerStyles = getFooterStylesByFormat(format);
  return footerStyles.length > 0 ? footerStyles[0].value : "HIDDEN";
};

export default function useFormatFooterStyles(styles, onStylesChange) {
  useEffect(() => {
    if (isFooterStyleAllowedForFormat(styles.format, styles.footerStyle))
      return;

    onStylesChange("footerStyle", getDefaultFooterStyleByFormat(styles.format));
  }, [styles.format, styles.footerStyle, onStylesChange]);

  const footerOptions = useMemo(() => {
    return getFooterStyleLabelsByFormat(styles.format);
  }, [styles.format]);

  const selectedFooterStyle = useMemo(() => {
    return (
      findFooterStyleByValue(styles.footerStyle) ||
      findFooterStyleByValue(getDefaultFooterStyleByFormat(styles.format))
    );
  }, [styles.footerStyle, styles.format]);

  return {
    footerOptions,
    selectedFooterStyle,
    getFooterStyleValueByLabel: (label) => findFooterStyleByLabel(label)?.value,
    isFooterStylePremiumByLabel: (label) =>
      Boolean(findFooterStyleByLabel(label)?.isPremium),
  };
}
